// ─── JMAP Client ──────────────────────────────────────────────────────────────
// Talks directly to Stalwart's JMAP endpoint using Basic auth.
// One instance per account — instantiated and stored in accountStore.

import type {
  JMAPSession, JMAPMailbox, JMAPEmail, JMAPThread,
  JMAPMethodCall, JMAPResponse, JMAPEmailSetResult, JMAPStateChange,
} from './jmapTypes'

// Properties fetched for the thread/inbox list (no body — keeps payload small)
const LIST_PROPERTIES = [
  'id', 'threadId', 'mailboxIds', 'keywords', 'size',
  'receivedAt', 'from', 'to', 'cc', 'subject', 'preview', 'hasAttachment',
]

// Additional properties fetched when opening a thread (includes body)
const BODY_PROPERTIES = [
  ...LIST_PROPERTIES,
  'blobId',
  'bodyStructure', 'bodyValues', 'textBody', 'htmlBody', 'attachments',
  'messageId', 'inReplyTo', 'references', 'replyTo', 'bcc', 'sentAt',
]

export class JMAPClient {
  private session: JMAPSession | null = null
  private authHeader: string
  private eventSource: EventSource | null = null
  private stateChangeCallback: ((change: JMAPStateChange) => void) | null = null

  constructor(
    private readonly baseUrl: string,
    private readonly email: string,
    private readonly password: string,
  ) {
    this.authHeader = `Basic ${btoa(`${email}:${password}`)}`
  }

  get accountEmail() { return this.email }

  // ─── Session ───────────────────────────────────────────────────────────────

  async connect(): Promise<JMAPSession> {
    const res = await fetch(`${this.baseUrl}/.well-known/jmap`, {
      headers: { Authorization: this.authHeader },
    })
    if (!res.ok) {
      if (res.status === 401) throw new Error('JMAP_AUTH_FAILED')
      throw new Error(`JMAP session fetch failed: ${res.status}`)
    }
    const session: JMAPSession = await res.json()
    // Rewrite session URLs to use the same origin as baseUrl (handles reverse-proxy mismatches)
    const origin = new URL(this.baseUrl).origin
    session.apiUrl = this.rewriteOrigin(session.apiUrl, origin)
    session.uploadUrl = this.rewriteOrigin(session.uploadUrl, origin)
    session.downloadUrl = this.rewriteOrigin(session.downloadUrl, origin)
    session.eventSourceUrl = this.rewriteOrigin(session.eventSourceUrl, origin)
    this.session = session
    return session
  }

  private rewriteOrigin(url: string, origin: string): string {
    try {
      // Preserve the base path from this.baseUrl (e.g. "/api") so that
      // Stalwart's session URLs like "/jmap/" become "/api/jmap/" and are
      // correctly routed through the Go API proxy by Caddy.
      const basePath = new URL(this.baseUrl).pathname.replace(/\/$/, '') // e.g. "/api"
      const u = new URL(url)
      return origin + basePath + u.pathname + u.search + u.hash
    } catch {
      return url
    }
  }

  private ensureSession(): JMAPSession {
    if (!this.session) throw new Error('JMAP client not connected — call connect() first')
    return this.session
  }

  get accountId(): string {
    return this.ensureSession().primaryAccounts['urn:ietf:params:jmap:mail'] ?? ''
  }

  // ─── Core request ──────────────────────────────────────────────────────────

  async request(calls: JMAPMethodCall[], using?: string[]): Promise<JMAPResponse> {
    const session = this.ensureSession()
    const body = JSON.stringify({
      using: using ?? ['urn:ietf:params:jmap:core', 'urn:ietf:params:jmap:mail'],
      methodCalls: calls,
    })
    const res = await fetch(session.apiUrl, {
      method: 'POST',
      headers: {
        Authorization: this.authHeader,
        'Content-Type': 'application/json',
      },
      body,
    })
    if (res.status === 401) {
      // Session may have changed — try re-connecting once
      await this.connect()
      const retry = await fetch(this.session!.apiUrl, {
        method: 'POST',
        headers: { Authorization: this.authHeader, 'Content-Type': 'application/json' },
        body,
      })
      if (!retry.ok) throw new Error('JMAP_AUTH_FAILED')
      return retry.json()
    }
    if (!res.ok) throw new Error(`JMAP request failed: ${res.status}`)
    return res.json()
  }

  // ─── Mailboxes ─────────────────────────────────────────────────────────────

  async listMailboxes(): Promise<JMAPMailbox[]> {
    const resp = await this.request([
      ['Mailbox/get', { accountId: this.accountId, ids: null }, '0'],
    ])
    const [, result] = resp.methodResponses[0]
    return (result.list as JMAPMailbox[]) ?? []
  }

  // ─── Thread list (inbox / folder view) ────────────────────────────────────

  async listThreads(mailboxId: string, opts: {
    position?: number
    limit?: number
    sort?: Array<{ property: string; isAscending: boolean }>
    filter?: Record<string, unknown>
  } = {}): Promise<{ emails: JMAPEmail[]; total: number; position: number }> {
    const { position = 0, limit = 50, sort, filter } = opts
    const queryArgs: Record<string, unknown> = {
      accountId: this.accountId,
      filter: filter ?? { inMailbox: mailboxId },
      sort: sort ?? [{ property: 'receivedAt', isAscending: false }],
      position,
      limit,
      calculateTotal: true,
    }
    const resp = await this.request([
      ['Email/query', queryArgs, '0'],
      ['Email/get', {
        accountId: this.accountId,
        '#ids': { resultOf: '0', name: 'Email/query', path: '/ids' },
        properties: LIST_PROPERTIES,
      }, '1'],
    ])

    const [, queryResult] = resp.methodResponses[0]
    const [, getResult] = resp.methodResponses[1]
    return {
      emails: (getResult.list as JMAPEmail[]) ?? [],
      total: (queryResult.total as number) ?? 0,
      position: (queryResult.position as number) ?? 0,
    }
  }

  // ─── Full thread fetch (with body) ─────────────────────────────────────────

  async getThread(threadId: string): Promise<{ thread: JMAPThread; emails: JMAPEmail[] }> {
    const resp = await this.request([
      ['Thread/get', { accountId: this.accountId, ids: [threadId] }, '0'],
      ['Email/get', {
        accountId: this.accountId,
        '#ids': { resultOf: '0', name: 'Thread/get', path: '/list/*/emailIds' },
        properties: BODY_PROPERTIES,
        fetchTextBodyValues: true,
        fetchHTMLBodyValues: true,
        maxBodyValueBytes: 256000,
      }, '1'],
    ])
    const [, threadResult] = resp.methodResponses[0]
    const [, emailResult] = resp.methodResponses[1]
    const threads = (threadResult.list as JMAPThread[]) ?? []
    return {
      thread: threads[0] ?? { id: threadId, emailIds: [] },
      emails: (emailResult.list as JMAPEmail[]) ?? [],
    }
  }

  // ─── Search ────────────────────────────────────────────────────────────────

  async searchEmails(query: string, opts: { limit?: number; position?: number } = {}): Promise<{
    emails: JMAPEmail[]
    total: number
  }> {
    const { limit = 50, position = 0 } = opts
    const resp = await this.request([
      ['Email/query', {
        accountId: this.accountId,
        filter: { text: query },
        sort: [{ property: 'receivedAt', isAscending: false }],
        position,
        limit,
        calculateTotal: true,
      }, '0'],
      ['Email/get', {
        accountId: this.accountId,
        '#ids': { resultOf: '0', name: 'Email/query', path: '/ids' },
        properties: LIST_PROPERTIES,
      }, '1'],
    ])
    const [, queryResult] = resp.methodResponses[0]
    const [, getResult] = resp.methodResponses[1]
    return {
      emails: (getResult.list as JMAPEmail[]) ?? [],
      total: (queryResult.total as number) ?? 0,
    }
  }

  // ─── Flag mutations ────────────────────────────────────────────────────────

  async setKeyword(emailIds: string[], keyword: string, value: boolean): Promise<void> {
    // Use JSON Pointer patch path ("keywords/$seen") so we only touch the one
    // flag and leave all other keywords intact. Setting null removes the keyword.
    const patchKey = `keywords/${keyword}`
    const patchVal: boolean | null = value ? true : null
    const update: Record<string, Record<string, boolean | null>> = {}
    for (const id of emailIds) {
      update[id] = { [patchKey]: patchVal }
    }
    await this.request([
      ['Email/set', { accountId: this.accountId, update }, '0'],
    ])
  }

  /** Set/unset a keyword on all emails in a thread.
   *  Fetches email IDs via Thread/get, then applies setKeyword. */
  async setThreadKeyword(threadId: string, keyword: string, value: boolean): Promise<void> {
    const resp = await this.request([
      ['Thread/get', { accountId: this.accountId, ids: [threadId] }, '0'],
    ])
    const [, threadResult] = resp.methodResponses[0]
    const thread = ((threadResult as { list: JMAPThread[] }).list)?.[0]
    if (!thread?.emailIds?.length) return
    await this.setKeyword(thread.emailIds, keyword, value)
  }

  // ─── Move to mailbox ───────────────────────────────────────────────────────

  /** Move emails to a mailbox and optionally set/unset JMAP keywords in one request.
   *  Pass keywords like { '$deleted': true } to set, { '$deleted': null } to unset. */
  async moveToMailbox(
    emailIds: string[],
    targetMailboxId: string,
    removeMailboxIds: string[] = [],
    keywords: Record<string, boolean | null> = {},
  ): Promise<void> {
    const update: Record<string, Record<string, unknown>> = {}
    for (const id of emailIds) {
      const patch: Record<string, boolean | null> = {
        [`mailboxIds/${targetMailboxId}`]: true,
      }
      for (const rm of removeMailboxIds) {
        patch[`mailboxIds/${rm}`] = null
      }
      for (const [kw, val] of Object.entries(keywords)) {
        patch[`keywords/${kw}`] = val
      }
      update[id] = patch
    }
    await this.request([
      ['Email/set', { accountId: this.accountId, update }, '0'],
    ])
  }

  /** Move all emails in a thread to targetMailbox, removing them from removeMailboxIds.
   *  Fetches email IDs via Thread/get so the caller only needs the threadId.
   *  Pass keywords like { '$deleted': true } to also set/unset flags atomically. */
  async moveThreadToMailbox(
    threadId: string,
    targetMailboxId: string,
    removeMailboxIds: string[] = [],
    keywords: Record<string, boolean | null> = {},
  ): Promise<void> {
    const resp = await this.request([
      ['Thread/get', { accountId: this.accountId, ids: [threadId] }, '0'],
    ])
    const [, threadResult] = resp.methodResponses[0]
    const thread = ((threadResult as { list: JMAPThread[] }).list)?.[0]
    if (!thread?.emailIds?.length) return
    await this.moveToMailbox(thread.emailIds, targetMailboxId, removeMailboxIds, keywords)
  }

  // ─── Send email ────────────────────────────────────────────────────────────

  async sendEmail(params: {
    from: { name: string; email: string }
    to: Array<{ name: string; email: string }>
    cc?: Array<{ name: string; email: string }>
    bcc?: Array<{ name: string; email: string }>
    subject: string
    bodyHtml: string
    bodyText: string
    inReplyTo?: string
    references?: string[]
    draftId?: string
    sentMailboxId: string
    draftsMailboxId: string
  }): Promise<{ emailId: string }> {
    const {
      from, to, cc, bcc, subject, bodyHtml, bodyText,
      inReplyTo, references, draftId, sentMailboxId, draftsMailboxId,
    } = params

    const calls: JMAPMethodCall[] = []

    // Create the email (in Sent folder)
    const createArgs: Record<string, unknown> = {
      mailboxIds: { [sentMailboxId]: true },
      from: [{ name: from.name, email: from.email }],
      to: to.map(a => ({ name: a.name, email: a.email })),
      cc: cc?.map(a => ({ name: a.name, email: a.email })),
      bcc: bcc?.map(a => ({ name: a.name, email: a.email })),
      subject,
      keywords: { '$seen': true },
      bodyValues: {
        html: { value: bodyHtml, charset: 'utf-8' },
        text: { value: bodyText, charset: 'utf-8' },
      },
      htmlBody: [{ partId: 'html', type: 'text/html' }],
      textBody: [{ partId: 'text', type: 'text/plain' }],
    }
    if (inReplyTo) createArgs['inReplyTo'] = [inReplyTo]
    if (references?.length) createArgs['references'] = references

    calls.push(['Email/set', {
      accountId: this.accountId,
      create: { email: createArgs },
    }, '0'])

    // Submit via EmailSubmission
    calls.push(['EmailSubmission/set', {
      accountId: this.accountId,
      create: {
        sub: {
          emailId: '#email',
          identityId: await this.getDefaultIdentityId(),
        },
      },
      onSuccessUpdateEmail: {
        '#sub': {
          [`mailboxIds/${draftsMailboxId}`]: null,
          [`mailboxIds/${sentMailboxId}`]: true,
          'keywords/$draft': null,
        },
      },
    }, '1'])

    // Destroy old draft if any
    if (draftId) {
      calls.push(['Email/set', {
        accountId: this.accountId,
        destroy: [draftId],
      }, '2'])
    }

    const resp = await this.request(calls)
    const [, createResult] = resp.methodResponses[0] as unknown as [string, JMAPEmailSetResult, string]
    const created = createResult.created?.['email']
    if (!created?.id && createResult.notCreated?.['email']) {
      throw new Error(`Send failed: ${createResult.notCreated['email'].description ?? 'unknown error'}`)
    }
    return { emailId: created?.id ?? '' }
  }

  // ─── Draft management ──────────────────────────────────────────────────────

  async saveDraft(params: {
    to: Array<{ name: string; email: string }>
    cc?: Array<{ name: string; email: string }>
    subject: string
    bodyHtml: string
    bodyText: string
    draftsMailboxId: string
    existingDraftId?: string
  }): Promise<string> {
    const { to, cc, subject, bodyHtml, bodyText, draftsMailboxId, existingDraftId } = params
    const calls: JMAPMethodCall[] = []

    if (existingDraftId) {
      calls.push(['Email/set', { accountId: this.accountId, destroy: [existingDraftId] }, '0'])
    }

    calls.push(['Email/set', {
      accountId: this.accountId,
      create: {
        draft: {
          mailboxIds: { [draftsMailboxId]: true },
          keywords: { '$draft': true },
          to: to.map(a => ({ name: a.name, email: a.email })),
          cc: cc?.map(a => ({ name: a.name, email: a.email })),
          subject,
          bodyValues: {
            html: { value: bodyHtml, charset: 'utf-8' },
            text: { value: bodyText, charset: 'utf-8' },
          },
          htmlBody: [{ partId: 'html', type: 'text/html' }],
          textBody: [{ partId: 'text', type: 'text/plain' }],
        },
      },
    }, existingDraftId ? '1' : '0'])

    const resp = await this.request(calls)
    const lastIdx = calls.length - 1
    const [, createResult] = resp.methodResponses[lastIdx] as unknown as [string, JMAPEmailSetResult, string]
    const created = createResult.created?.['draft']
    return created?.id ?? ''
  }

  async deleteDraft(emailId: string): Promise<void> {
    await this.request([
      ['Email/set', { accountId: this.accountId, destroy: [emailId] }, '0'],
    ])
  }

  // ─── Identity (sender) ─────────────────────────────────────────────────────

  async listIdentities(): Promise<Array<{ id: string; name: string; email: string }>> {
    const resp = await this.request([
      ['Identity/get', { accountId: this.accountId, ids: null }, '0'],
    ])
    const [, result] = resp.methodResponses[0]
    return (result.list as Array<{ id: string; name: string; email: string }>) ?? []
  }

  private cachedIdentityId: string | null = null
  async getDefaultIdentityId(): Promise<string> {
    if (this.cachedIdentityId) return this.cachedIdentityId
    const identities = await this.listIdentities()
    // Prefer the identity matching the logged-in email
    const match = identities.find(i => i.email === this.email) ?? identities[0]
    this.cachedIdentityId = match?.id ?? ''
    return this.cachedIdentityId
  }

  // ─── Blob / attachment download ────────────────────────────────────────────

  getBlobUrl(blobId: string): string {
    const session = this.ensureSession()
    // rewriteOrigin() passes the template through `new URL()` which URL-encodes
    // `{` → `%7B` and `}` → `%7D`.  Handle both forms with a regex replace.
    const sub = (tpl: string, key: string, val: string) =>
      tpl.replace(new RegExp(`(?:\\{|%7B)${key}(?:\\}|%7D)`, 'i'), val)

    let url = session.downloadUrl
    url = sub(url, 'accountId', encodeURIComponent(this.accountId))
    url = sub(url, 'blobId',    encodeURIComponent(blobId))
    url = sub(url, 'name',      'message.eml')
    url = sub(url, 'type',      encodeURIComponent('message/rfc822'))
    return url
  }

  // Fetch raw blob text with auth — use this instead of bare fetch() on getBlobUrl()
  async downloadBlob(blobId: string): Promise<string> {
    const url = this.getBlobUrl(blobId)
    const res = await fetch(url, { headers: { Authorization: this.authHeader } })
    if (!res.ok) throw new Error(`${res.status} ${res.statusText}`)
    return res.text()
  }

  // ─── EventSource push ──────────────────────────────────────────────────────

  isPushConnected(): boolean {
    return this.eventSource !== null && this.eventSource.readyState !== EventSource.CLOSED
  }

  connectPush(onStateChange: (change: JMAPStateChange) => void): boolean {
    if (!this.session?.eventSourceUrl) return false
    this.stateChangeCallback = onStateChange
    this.openEventSource()
    return true
  }

  private openEventSource() {
    if (!this.session?.eventSourceUrl) return
    // rewriteOrigin() passes the URL through `new URL()` which percent-encodes
    // `{` → `%7B` and `}` → `%7D`, so simple string.replace() won't match.
    const sub = (tpl: string, key: string, val: string) =>
      tpl.replace(new RegExp(`(?:\\{|%7B)${key}(?:\\}|%7D)`, 'i'), val)
    let url = this.session.eventSourceUrl
    url = sub(url, 'types', 'Email,Mailbox,Thread')
    url = sub(url, 'closeAfter', 'no')
    url = sub(url, 'ping', '90')
    const sep = url.includes('?') ? '&' : '?'
    url += `${sep}_auth=${encodeURIComponent(this.authHeader)}`

    this.eventSource?.close()
    this.eventSource = new EventSource(url)

    this.eventSource.addEventListener('state', (e: MessageEvent) => {
      try {
        const change: JMAPStateChange = JSON.parse(e.data)
        this.stateChangeCallback?.(change)
      } catch {
        // ignore malformed events
      }
    })

    // The Go proxy keeps this connection alive and reconnects to Stalwart
    // internally — the browser should never see this error under normal conditions.
    // Only reconnect on real network failures (e.g. internet outage).
    this.eventSource.onerror = () => {
      this.eventSource?.close()
      this.eventSource = null
      setTimeout(() => {
        if (this.stateChangeCallback) this.openEventSource()
      }, 5_000)
    }
  }

  disconnectPush() {
    this.eventSource?.close()
    this.eventSource = null
    this.stateChangeCallback = null
  }

  // ─── Utility ───────────────────────────────────────────────────────────────

  /** Find mailbox by role (e.g. 'inbox', 'sent', 'drafts', 'trash', 'junk') */
  findMailboxByRole(mailboxes: JMAPMailbox[], role: string): JMAPMailbox | undefined {
    return mailboxes.find(m => m.role === role)
  }

  /** Find mailbox by slug-name (for custom folders) */
  findMailboxByName(mailboxes: JMAPMailbox[], name: string): JMAPMailbox | undefined {
    return mailboxes.find(m => m.name.toLowerCase() === name.toLowerCase())
  }
}
